import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBars,
  faBoxesStacked,
  faBullhorn,
  faCalendarCheck,
  faChartPie,
  faCheck,
  faConciergeBell,
  faHeadset,
  faPlay,
  faRocket,
  faStethoscope,
  faTimes,
  faUserShield,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import logoImage from '../img/logo.png';
import featureImage1 from '../img/placeholder-agenda.png';
import featureImage2 from '../img/placeholder-financeiro.png';
import '../styles/LandingPageSales.css';
import { listPublicPlans } from '../services/salesContractApi';

const NAV_LINKS = [
  { label: 'Módulos', href: '#modules' },
  { label: 'Funcionalidades', href: '#features' },
  { label: 'Como funciona', href: '#how-it-works' },
  { label: 'Investimento', href: '#pricing' },
];

const PLAN_PRESENTATION = [
  {
    id: 'essencial',
    eyebrow: 'Para organizar o atendimento',
    name: 'Essencial',
    description: 'Para clínicas que querem começar pelos fluxos essenciais da rotina.',
    features: [
      'Agenda e fluxo de atendimento.',
      'Pacientes e prontuário.',
      'Cadastro de equipe e serviços.',
      'Orientação inicial de ativação.',
    ],
    cta: 'Conhecer o Essencial',
  },
  {
    id: 'gestao-integrada',
    eyebrow: 'Melhor equilíbrio',
    name: 'Gestão Integrada',
    description: 'Para centralizar a operação e acompanhar a clínica de ponta a ponta.',
    features: [
      'Tudo do plano Essencial.',
      'Financeiro e indicadores.',
      'Estoque e materiais integrados.',
      'Marketing, leads e funil.',
      'Perfis de acesso e implantação assistida.',
    ],
    cta: 'Agendar demonstração',
    recommended: true,
  },
  {
    id: 'personalizado',
    eyebrow: 'Para operações específicas',
    name: 'Personalizado',
    description: 'Para clínicas que precisam de implantação ou acompanhamento fora do escopo padrão.',
    features: [
      'Todos os módulos da Gestão Integrada.',
      'Escopo avaliado individualmente.',
      'Migração conforme análise dos arquivos.',
      'Treinamento ampliado definido em proposta.',
      'Acompanhamento conforme contrato.',
    ],
    cta: 'Solicitar proposta',
    pricePrefix: 'A partir de',
    custom: true,
  },
];

const formatPlanPrice = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
}).format(Number(value || 0));

const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrolled;
};

const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.12 });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, inView];
};

const Navbar = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const foreground = scrolled ? '#334155' : '#ffffff';

  return (
    <nav className="landing-navbar" style={{
      position: 'fixed', inset: '0 0 auto 0', zIndex: 1000, padding: '0 24px',
      backgroundColor: scrolled ? 'rgba(255,255,255,0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(14px)' : 'none',
      borderBottom: scrolled ? '1px solid #e2e8f0' : '1px solid transparent',
      transition: 'all 0.25s ease',
    }}>
      <div className="landing-navbar__inner">
        <Link to="/inicio" aria-label="Ir para o início">
          <img src={logoImage} alt="Fluxo Clínica" className="landing-navbar__logo" />
        </Link>
        <div className="landing-navbar__links">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} style={{ color: foreground }}>{link.label}</a>
          ))}
        </div>
        <div className="landing-navbar__actions">
          <Link to="/login" style={{ color: foreground }} className="landing-navbar__login">Entrar</Link>
          <Link to="/demonstracao?plano=gestao-integrada" className="landing-button landing-button--small landing-button--success">
            Agendar demonstração
          </Link>
        </div>
        <button
          type="button"
          className="landing-navbar__toggle"
          onClick={() => setOpen((current) => !current)}
          aria-label={open ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={open}
          style={{ color: foreground }}
        >
          <FontAwesomeIcon icon={open ? faTimes : faBars} />
        </button>
      </div>
      {open && (
        <div className="landing-navbar__mobile-menu">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} onClick={() => setOpen(false)}>{link.label}</a>
          ))}
          <Link to="/login" onClick={() => setOpen(false)}>Entrar</Link>
          <Link to="/demonstracao?plano=gestao-integrada" onClick={() => setOpen(false)} className="landing-button landing-button--success">
            Agendar demonstração
          </Link>
        </div>
      )}
    </nav>
  );
};

const StatBadge = ({ value, label }) => (
  <div className="landing-stat"><strong>{value}</strong><span>{label}</span></div>
);

const ModuleCard = ({ icon, name, description, color }) => (
  <article className="landing-module-card">
    <span style={{ color, backgroundColor: `${color}14`, borderColor: `${color}28` }}>
      <FontAwesomeIcon icon={icon} />
    </span>
    <h3>{name}</h3>
    <p>{description}</p>
  </article>
);

const ProductPreview = ({ feature }) => (
  <div className="landing-product-preview" aria-label={`Resumo de ${feature.title}`}>
    <div className="landing-product-preview__topbar"><span /><span /><span /></div>
    <div className="landing-product-preview__content">
      <span className="landing-product-preview__icon"><FontAwesomeIcon icon={feature.icon} /></span>
      <small>Fluxo integrado</small>
      <strong>{feature.previewTitle}</strong>
      <div>
        {feature.previewItems.map((item) => (
          <p key={item}><FontAwesomeIcon icon={faCheck} />{item}</p>
        ))}
      </div>
    </div>
  </div>
);

const FeatureSection = ({ feature, index }) => {
  const [ref, inView] = useInView();
  const isEven = index % 2 === 0;

  return (
    <article ref={ref} className={`landing-feature ${inView ? 'is-visible' : ''}`}>
      <div className="landing-feature__text" style={{ order: isEven ? 1 : 2 }}>
        <span className="landing-section-kicker"><FontAwesomeIcon icon={feature.icon} />{feature.kicker}</span>
        <h3>{feature.title}</h3>
        <p>{feature.description}</p>
        <ul>
          {feature.items.map((item) => (
            <li key={item}><span><FontAwesomeIcon icon={faCheck} /></span>{item}</li>
          ))}
        </ul>
        <Link to="/demonstracao" className="landing-text-link">
          Ver em uma demonstração <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
      <div className="landing-feature__media" style={{ order: isEven ? 2 : 1 }}>
        {feature.image
          ? <img src={feature.image} alt={feature.imageAlt} />
          : <ProductPreview feature={feature} />}
      </div>
    </article>
  );
};

const LandingPage = () => {
  const [pricingPlans, setPricingPlans] = useState([]);

  useEffect(() => {
    listPublicPlans().then(({ data }) => {
      setPricingPlans(data.map((plan) => ({
        ...PLAN_PRESENTATION.find((item) => item.id === plan.slug),
        ...plan,
        id: plan.slug,
        monthlyPrice: Number(plan.price),
        implantationPrice: Number(plan.implementation_price || 0),
        firstPayment: Number(plan.price) + Number(plan.implementation_price || 0),
        recommended: Boolean(plan.is_recommended),
      })));
    }).catch(() => setPricingPlans([]));
  }, []);

  const recommendedPlan = pricingPlans.find((plan) => plan.recommended) || pricingPlans[0];
  const modules = [
    { icon: faStethoscope, name: 'Consultório', description: 'Agenda, atendimento e prontuário.', color: '#2563eb' },
    { icon: faUsers, name: 'Pacientes e equipe', description: 'Cadastros e perfis organizados.', color: '#7c3aed' },
    { icon: faChartPie, name: 'Financeiro', description: 'Receitas, despesas e indicadores.', color: '#059669' },
    { icon: faBoxesStacked, name: 'Estoque', description: 'Materiais e movimentações integrados.', color: '#0d9488' },
    { icon: faBullhorn, name: 'Marketing', description: 'Leads, funil e acompanhamento comercial.', color: '#db2777' },
    { icon: faConciergeBell, name: 'Serviços', description: 'Procedimentos e organização operacional.', color: '#f59e0b' },
    { icon: faUserShield, name: 'Acessos', description: 'Perfis e permissões por função.', color: '#dc2626' },
  ];

  const features = [
    {
      icon: faCalendarCheck,
      kicker: 'Rotina clínica',
      title: 'Agenda e pacientes conectados ao atendimento',
      description: 'Organize os horários da equipe e mantenha as informações do paciente acessíveis no fluxo da consulta.',
      items: [
        'Visualizações de agenda por período e profissional.',
        'Cadastro e histórico do paciente no mesmo ambiente.',
        'Fluxo de recepção e atendimento por perfis de acesso.',
      ],
      image: featureImage1,
      imageAlt: 'Visualização do módulo de agenda do Fluxo Clínica',
    },
    {
      icon: faChartPie,
      kicker: 'Visão financeira',
      title: 'Decisões com base no que entra e no que sai',
      description: 'Acompanhe o desempenho da clínica com informações financeiras ligadas à operação do dia a dia.',
      items: [
        'Faturamento por período, serviço e paciente.',
        'Acompanhamento de recebimentos e pendências.',
        'Indicadores para apoiar decisões de gestão.',
      ],
      image: featureImage2,
      imageAlt: 'Visualização do módulo financeiro do Fluxo Clínica',
    },
    {
      icon: faBullhorn,
      kicker: 'Marketing',
      title: 'Do novo interessado ao acompanhamento comercial',
      description: 'Centralize os leads da clínica e acompanhe cada oportunidade sem depender de controles paralelos.',
      items: [
        'Captura e organização de novos interessados.',
        'Funil visual para acompanhar cada etapa.',
        'Histórico de interações e prioridades do atendimento.',
      ],
      previewTitle: 'Leads em acompanhamento',
      previewItems: ['Novos contatos', 'Demonstrações agendadas', 'Próximas ações'],
    },
    {
      icon: faBoxesStacked,
      kicker: 'Operação integrada',
      title: 'Estoque e serviços acompanhando a rotina',
      description: 'Tenha mais previsibilidade sobre materiais utilizados e serviços realizados pela equipe.',
      items: [
        'Entradas, saídas e alertas de estoque.',
        'Materiais relacionados aos serviços da clínica.',
        'Permissões para proteger ações sensíveis.',
      ],
      previewTitle: 'Operação sob controle',
      previewItems: ['Materiais atualizados', 'Serviços organizados', 'Alertas importantes'],
    },
  ];

  const steps = [
    { number: '01', icon: faCalendarCheck, title: 'Agende a demonstração', text: 'Conte sua necessidade e veja os fluxos mais importantes para sua operação.' },
    { number: '02', icon: faPlay, title: 'Avalie o teste', text: 'Quando fizer sentido, liberamos um teste controlado de 7 dias por convite.' },
    { number: '03', icon: faRocket, title: 'Contrate e implante', text: 'Após o pagamento inicial, configuramos a clínica e iniciamos a implantação assistida.' },
  ];

  return (
    <div className="landing-page">
      <Navbar />
      <main>
        <section className="landing-hero">
          <div className="landing-hero__glow landing-hero__glow--one" />
          <div className="landing-hero__glow landing-hero__glow--two" />
          <div className="landing-hero__content">
            <span className="landing-hero__badge"><span />{recommendedPlan ? `Plano recomendado: ${formatPlanPrice(recommendedPlan.monthlyPrice)}/mês + implantação de ${formatPlanPrice(recommendedPlan.implantationPrice)}` : 'Planos para cada fase da sua clínica'}</span>
            <h1>Gestão integrada para sua clínica crescer com <em>mais organização</em></h1>
            <p>Agenda, pacientes, financeiro, estoque e marketing em um único sistema, com implantação acompanhada para você começar com segurança.</p>
            <div className="landing-hero__actions">
              <Link to="/demonstracao?plano=gestao-integrada" className="landing-button landing-button--success landing-button--large">
                Agendar demonstração <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <Link to="/demonstracao?interesse=teste&plano=gestao-integrada" className="landing-button landing-button--ghost landing-button--large">
                <FontAwesomeIcon icon={faPlay} /> Solicitar teste de 7 dias
              </Link>
            </div>
            <p className="landing-hero__note">A solicitação não cria conta nem inicia cobrança. Primeiro entendemos sua clínica.</p>
            <div className="landing-hero__stats">
              {recommendedPlan && <StatBadge value={formatPlanPrice(recommendedPlan.monthlyPrice)} label="mensalidade" />}
              {recommendedPlan && <StatBadge value={formatPlanPrice(recommendedPlan.implantationPrice)} label="implantação, uma vez" />}
              <StatBadge value="7 dias" label="teste por convite" />
              <StatBadge value="6 meses" label="em operação piloto" />
            </div>
            <div className="landing-hero__trust">
              <span><FontAwesomeIcon icon={faHeadset} /> Implantação acompanhada</span>
              <span><FontAwesomeIcon icon={faUserShield} /> Perfis de acesso</span>
              <span><FontAwesomeIcon icon={faCheck} /> Teste controlado por convite</span>
            </div>
          </div>
        </section>

        <section id="modules" className="landing-section landing-section--white">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Operação integrada</span>
              <h2>O trabalho da clínica conectado de ponta a ponta</h2>
              <p>Os módulos compartilham informações para reduzir controles separados e dar mais clareza à gestão.</p>
            </header>
            <div className="landing-modules-grid">
              {modules.map((module) => <ModuleCard key={module.name} {...module} />)}
            </div>
          </div>
        </section>

        <section id="features" className="landing-section landing-section--soft">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Funcionalidades</span>
              <h2>Mais organização para atender e administrar</h2>
              <p>Conheça os principais fluxos e veja na demonstração como eles se adaptam à realidade da sua clínica.</p>
            </header>
            <div className="landing-features-list">
              {features.map((feature, index) => <FeatureSection key={feature.title} feature={feature} index={index} />)}
            </div>
          </div>
        </section>

        <section className="landing-proof">
          <div className="landing-container landing-proof__grid">
            <div>
              <span className="landing-section-kicker landing-section-kicker--light">Validado na rotina</span>
              <h2>Construído com aprendizado de uma clínica em operação real</h2>
              <p>A primeira versão está em uso há seis meses. Esse período permitiu ajustar os fluxos de agenda, pacientes, financeiro, estoque e gestão com base no uso diário.</p>
            </div>
            <div className="landing-proof__card">
              <strong>6 meses</strong>
              <span>de operação piloto acompanhada</span>
              <hr />
              <p>Uma base mais madura para iniciar novas implantações e continuar evoluindo com cada clínica.</p>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="landing-section landing-section--white">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Próximos passos</span>
              <h2>Conheça antes de tomar a decisão</h2>
              <p>O processo começa por uma conversa, sem cadastro extenso e sem ativação automática.</p>
            </header>
            <div className="landing-steps">
              {steps.map((step) => (
                <article key={step.number} className="landing-step-card">
                  <small>{step.number}</small><span><FontAwesomeIcon icon={step.icon} /></span>
                  <h3>{step.title}</h3><p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="landing-section landing-section--soft">
          <div className="landing-container">
            <header className="landing-section__header">
              <span className="landing-section-kicker">Investimento transparente</span>
              <h2>Escolha o nível de acompanhamento da sua clínica</h2>
              <p>O Gestão Integrada reúne a operação completa pelo melhor equilíbrio entre investimento e recursos.</p>
            </header>
            <div className="landing-pricing-grid">
              {pricingPlans.map((plan) => (
                <article
                  key={plan.id}
                  className={`landing-plan-card${plan.recommended ? ' landing-plan-card--recommended' : ''}`}
                >
                  {plan.recommended && (
                    <span className="landing-plan-card__badge">Recomendado para gestão completa</span>
                  )}
                  <span className="landing-plan-card__eyebrow">{plan.eyebrow}</span>
                  <h3>{plan.name}</h3>
                  <p className="landing-plan-card__description">{plan.description}</p>
                  <div className="landing-plan-card__price">
                    {plan.pricePrefix && <span>{plan.pricePrefix}</span>}
                    <div><strong>{formatPlanPrice(plan.monthlyPrice)}</strong><em>/mês</em></div>
                  </div>
                  <div className="landing-plan-card__implantation">
                    Implantação: <strong>{formatPlanPrice(plan.implantationPrice)}</strong>
                  </div>
                  <ul>
                    {plan.features.map((item) => (
                      <li key={item}><FontAwesomeIcon icon={faCheck} /> <span>{item}</span></li>
                    ))}
                  </ul>
                  <div className="landing-plan-card__summary">
                    <span>Primeira contratação</span>
                    <strong>{formatPlanPrice(plan.firstPayment)}</strong>
                  </div>
                  <Link
                    to={`/demonstracao?plano=${plan.id}`}
                    className={`landing-button landing-button--large ${plan.recommended ? 'landing-button--success' : 'landing-button--outline'}`}
                  >
                    {plan.cta} <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                  {plan.recommended && (
                    <Link
                      to="/demonstracao?interesse=teste&plano=gestao-integrada"
                      className="landing-text-link landing-text-link--center"
                    >
                      Solicitar o teste de 7 dias
                    </Link>
                  )}
                </article>
              ))}
            </div>
            <p className="landing-pricing-note">
              A demonstração é gratuita. A contratação e o teste só avançam depois de entendermos a sua clínica.
            </p>
          </div>
        </section>

        <section className="landing-final-cta">
          <div className="landing-container">
            <span className="landing-section-kicker landing-section-kicker--light">Veja funcionando</span>
            <h2>Vamos conversar sobre a rotina da sua clínica?</h2>
            <p>Agende uma demonstração e conheça o sistema antes de criar qualquer conta.</p>
            <Link to="/demonstracao?plano=gestao-integrada" className="landing-button landing-button--success landing-button--large">
              Agendar demonstração <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <small>{recommendedPlan ? `${formatPlanPrice(recommendedPlan.monthlyPrice)}/mês · implantação ${formatPlanPrice(recommendedPlan.implantationPrice)} · teste por convite` : 'Consulte os planos disponíveis'}</small>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container landing-footer__content">
          <div><img src={logoImage} alt="Fluxo Clínica" /><p>Gestão integrada para clínicas, com implantação acompanhada.</p></div>
          <nav aria-label="Links do rodapé">
            <a href="#modules">Módulos</a><a href="#features">Funcionalidades</a>
            <a href="#how-it-works">Como funciona</a><a href="#pricing">Investimento</a>
            <Link to="/demonstracao">Demonstração</Link><Link to="/login">Entrar</Link>
          </nav>
        </div>
        <div className="landing-container landing-footer__bottom">© {new Date().getFullYear()} Fluxo Clínica. Todos os direitos reservados.</div>
      </footer>
    </div>
  );
};

export default LandingPage;
